// The host port — the whole of what a trading control needs from whoever
// embeds it.
//
// A control is written against this interface and nothing else: it never
// imports a translator, a preference singleton, a panel registry or a docking
// manager, and it never reaches a window global. A host — a terminal page, a
// backoffice screen, a demo harness — supplies one object shaped like
// `TradingHost` per control instance, and that object is the only way out of
// the control.
//
// This module imports nothing but the row shapes it names. It is the contract,
// so it must be readable by a host that shares none of the package's internals.
//
// Everything here is REQUIRED, and `assertHost` proves it at construction.
// There is no optional member and no default, because a half-supplied host is
// how a control half-works: it renders, it looks alive, and the one capability
// nobody wired is discovered by a user clicking something that does nothing.
// A missing member has to be a loud failure at wiring time, not a quiet one at
// click time.
//
// Every member here has a caller among the controls this package ships. The
// seven that once looked speculative — `spawn`, `persistState`, `saveLayout`,
// `log`, `trading.pickInstrument`, `marketData.resubscribe` and
// `marketData.getOrders` — are the order book's and the trade feed's: a ladder
// recovers from a sequence gap with `resubscribe` without unsubscribing its
// neighbours, badges its own resting size from `getOrders`, reports the wire
// anomalies a user cannot see through `log`, and retargets itself through
// `pickInstrument`; both of them `spawn` a sibling and record what they
// remember with `persistState` + `saveLayout`.
//
// The cost of "everything is required" is still real and worth stating: a host
// adopting a subset must supply members that subset never calls. They are cheap
// (a no-op `spawn`, a `log` that forwards to the console) — but they are not
// free, and an adopter who supplies stubs is not doing anything wrong.
import type { InstrumentRow, OrderRow, OrderSide, OrderStatus, OrderType, QuoteStats, TradeRow } from './trading-data.js';

/// The colours a control has to paint with as values rather than as class
/// names.
///
/// A class name cannot reach a `<canvas>`: the pixels are drawn by the control,
/// so the palette has to arrive as strings. Every other look in this package is
/// a class the host's stylesheet gives meaning to, and this is the one place
/// that arrangement cannot hold — which is why the colours come from the host
/// here too, rather than being written into the package as literals the host
/// never chose. Each value is any CSS colour the host's palette uses; a control
/// varies the opacity itself, so a solid colour is the expected answer.
export interface CanvasPalette {
    /// The rising / bid / buy direction — the colour behind `side-buy`.
    up: string;
    /// The falling / ask / sell direction — the colour behind `side-sell`.
    down: string;
    /// Dividers, gridlines and axes drawn on a canvas — axis labels included.
    grid: string;
    /// Type for the text a control draws on a canvas, as the `font` shorthand
    /// (`'10px monospace'`). A canvas inherits nothing from the page and
    /// resolves no custom property, so a font is the one measurement the host
    /// has to state as a value; the alternative is the package picking a size
    /// and a family the host's design system never chose.
    font: string;
}

/// How a host words and colours the trading vocabulary. The control owns the
/// data; the host owns the language and the stylesheet, so it says what a side
/// or a status reads as and which CSS class carries its colour.
///
/// The two class methods are the one place a host has to agree with the shipped
/// stylesheet rather than merely supply values to it. A control puts whatever
/// string comes back straight onto the cell without inspecting it, so nothing
/// in `src/` ever emits these names and `styles/trading-controls.css` cannot be
/// read to discover them. They are named here, and `PRESENTATION_CLASSES` below
/// is the same list as data — `tools/check-style-contract.mjs` asserts the
/// stylesheet styles every one of them, so the agreement is checked rather than
/// discovered by a cell that came out unstyled.
export interface TradingPresentation {
    /// Localized "Buy" / "Sell".
    sideText(side: OrderSide): string;
    /// Is this side a buy? The same knowledge `sideText` and `sideClass`
    /// already need, asked for as a predicate: a control that has to put a row
    /// on the bid or the ask half of a book is deciding structure, not wording,
    /// and the alternative is each control spelling out which of `0`, `'Buy'`
    /// and `'BUY'` the wire it happens to be fed uses.
    isBuy(side: OrderSide): boolean;
    /// Localized short order type — "LMT", "MKT", "STP", "STP-LMT". Needs both
    /// prices because a conditional order is only a stop-limit when it carries
    /// a limit price as well as a stop price.
    typeText(type: OrderType, limitPrice: number, stopPrice: number): string;
    /// Localized order status.
    statusText(status: OrderStatus): string;
    /// A moment, worded. The host owns this for the same reason it owns a side or a
    /// status: what a time should read as is not a property of the data. A live tape wants
    /// the time of day in the reader's own zone; a blotter over a finished run wants the
    /// date too, and in the zone the run was recorded in, or every row reads as the same
    /// unplaceable day. `formatTime` is this package's tape answer, exported so a host that
    /// wants exactly that need not restate it.
    timeText(value: string | number | Date): string;
    /// CSS class colouring a cell by side. The shipped stylesheet styles
    /// `side-buy` and `side-sell`; a host on its own palette may answer with
    /// its own names and style those instead.
    sideClass(side: OrderSide): string;
    /// CSS class colouring a cell by the sign of a P&L figure. The shipped
    /// stylesheet styles `pnl-positive` and `pnl-negative`; the empty string is
    /// a legitimate answer for "no colour".
    pnlClass(pnl: number): string;
    /// The palette for the parts a control draws itself. Read per paint, so a
    /// host that switches theme repaints in the new colours without the control
    /// being told a theme exists.
    canvasPalette(): CanvasPalette;
}

/// The class names `TradingPresentation` is expected to return, as data.
///
/// Exported because it is a contract, not a detail: a host writing its own
/// presentation reads this to know which names the shipped stylesheet already
/// paints, and the style-contract check reads it to prove they are all styled.
export const PRESENTATION_CLASSES = {
    sideClass: ['side-buy', 'side-sell'],
    pnlClass: ['pnl-positive', 'pnl-negative'],
} as const;

/// A string-keyed store. Two of these reach a control and they are NOT
/// interchangeable — see `TradingHost.preferences` and `TradingHost.cache`.
export interface HostStore {
    /// The stored value, or `fallback` when the key was never written.
    get(key: string, fallback: string | null): string | null;
    /// Write, or clear by writing null.
    set(key: string, value: string | null): void;
}

/// The read side of the trading account, as far as a control is concerned.
/// Narrow on purpose: a control asks for the few calls it makes rather than
/// being handed a whole API client to explore.
export interface TradingApi {
    /// Executed trades, newest first, for one portfolio. A null `symbol` means
    /// every instrument — the argument is stated either way rather than being
    /// left off, so a call site says which of the two it wants.
    getExecutions(portfolioId: number, symbol: string | null, limit: number): Promise<TradeRow[]>;
    /// Instruments matching a query; the empty query means "everything".
    searchInstruments(query: string): Promise<InstrumentRow[]>;
}

/// How much of a symbol a control wants streamed. A quote row needs the last
/// price, a tape needs every print, a ladder needs the book as well — and each
/// costs the wire something different, so a control states which it is asking
/// for rather than leaving the argument off and taking a default.
export const MarketDataLevels = {
    /// Level 1 only: best bid/ask and last price.
    Quotes: 'l1only',
    /// Level 1 plus every tick.
    Tape: 'tape',
    /// Level 1, ticks and market depth.
    Full: 'full',
} as const;

export type MarketDataLevel = typeof MarketDataLevels[keyof typeof MarketDataLevels];

/// The live market-data connection. `addSymbol` / `removeSymbol` are
/// refcounted by the implementation for each kind of data a level brings, so
/// two controls watching one symbol hold one subscription between them, neither
/// can unsubscribe the other, and the one asking for more gets it. A control
/// gives back exactly what it took: the same symbol at the same level, once.
export interface MarketDataClient {
    addSymbol(symbol: string, level: MarketDataLevel): Promise<unknown>;
    removeSymbol(symbol: string, level: MarketDataLevel): Promise<unknown>;
    /// Make the server resend `symbol` from scratch, without touching the
    /// refcount. A control applying diffs against a sequence cannot recover
    /// from a missed frame on its own — the only cure is a fresh snapshot, and
    /// asking for one must not unsubscribe the other controls watching the
    /// same symbol.
    resubscribe(symbol: string, level: MarketDataLevel): Promise<void>;
    /// The client's cache of this session's own orders. The order book paints
    /// its "you have size here" badge from this rather than subscribing again.
    getOrders(): OrderRow[];
}

/// What a control reads about the account and the instrument universe.
export interface TradingContext {
    readonly api: TradingApi;
    readonly marketData: MarketDataClient;
    /// The portfolio a control should load against, read per use rather than
    /// captured — the user can switch portfolio under a control that is
    /// already on screen.
    portfolioId(): number | null;
    /// Ask the host to let the user pick an instrument. The host owns the
    /// picker UI; the control only learns what was chosen, and learns nothing
    /// if the user dismisses it.
    pickInstrument(onPicked: (symbol: string) => void): void;
    /// The step an instrument's price moves by, or null while the host does
    /// not know it. A control writes the instrument's prices to the step's
    /// precision, and by their magnitude without one.
    priceStep(symbol: string): number | null;
}

/// Where a control's summary of what it is showing goes. A status-bar ticker is
/// page chrome outside any control, so a control reports and the host decides
/// what to do with the report.
export interface TickerSink {
    publish(symbols: string[], stats: Map<string, QuoteStats>): void;
}

/// One live control instance, from the host's side. Deliberately opaque: the
/// host holds a heterogeneous set and only ever hands instances back to the
/// control that asked for them, which is what `broadcast<T>` expresses.
export type TradingControl = object;

/// Everything a trading control needs from its host.
export interface TradingHost {
    // -- this instance's role on the page ---------------------------------
    /// True when this instance is the one that speaks for the page. A page has
    /// one status-bar ticker and a control can be duplicated, so somebody has
    /// to decide which duplicate feeds it — and that is host policy, not
    /// something a control can work out about itself.
    readonly isPrimary: boolean;

    // -- language and wording ---------------------------------------------
    /// Translate. `key` is the English source text; `{0}`, `{1}` … in the
    /// translation are replaced positionally from `args`.
    t(key: string, ...args: unknown[]): string;
    readonly presentation: TradingPresentation;

    // -- storage ------------------------------------------------------------
    /// Settings that belong to the user and are expected to survive — a chosen
    /// depth, a view mode, a favourites list. May be slow and may be shared
    /// across devices.
    readonly preferences: HostStore;
    /// Local scratch that may be thrown away — a per-day price baseline, a
    /// memoised computation. Deliberately a different store from
    /// `preferences`: writing high-churn per-symbol keys into a synced
    /// per-user settings blob is how a settings row becomes a cache.
    readonly cache: HostStore;

    // -- the trading context it reads ---------------------------------------
    readonly trading: TradingContext;

    // -- where its ticker updates go ----------------------------------------
    readonly ticker: TickerSink;

    // -- permission and diagnostics -----------------------------------------
    /// May the user do this? `action` is the English name of the attempt
    /// ("add to favorites"); the host translates it and may show a sign-in
    /// prompt before answering false.
    allow(action: string): boolean;
    /// Send a diagnostic line wherever this host collects them. Controls use
    /// it for wire anomalies the user cannot see and support has to.
    log(message: string): void;

    // -- lifecycle calls it makes back --------------------------------------
    /// This control wants to go away.
    close(): void;
    /// The `+` gesture: another control of the same kind, starting from
    /// `state`. The host knows what kind this one is, so the control does not
    /// name its own type.
    spawn(state: Record<string, unknown>): void;
    /// Remember this control's per-instance state. Recording only — call
    /// `saveLayout` when the change is worth flushing.
    persistState(patch: Record<string, unknown>): void;
    /// Write the layout out now, bypassing any debounce the host applies.
    saveLayout(): void;

    // -- the host's handle on live instances --------------------------------
    /// Announce this instance. A host fans incoming data to every live control
    /// of a kind, so an unregistered control is a control that never updates.
    register(control: TradingControl): void;
    unregister(control: TradingControl): void;
    /// Apply something to every live control of this kind, this one included.
    /// The caller knows what its own siblings are, which is why `T` is its to
    /// name.
    broadcast<T>(apply: (control: T) => void): void;
}

/// Prove a host object satisfies the port, and say precisely what is missing
/// when it does not.
///
/// Called from every control's constructor before anything else, so a
/// mis-wired host fails at construction with the member named, instead of at
/// the first click that needed it.
export function assertHost(host: TradingHost, controlName: string): TradingHost {
    _required(host, 'host', 'object', controlName);

    _required(host.isPrimary, 'host.isPrimary', 'boolean', controlName);
    _required(host.t, 'host.t', 'function', controlName);

    _required(host.presentation, 'host.presentation', 'object', controlName);
    _required(host.presentation.sideText, 'host.presentation.sideText', 'function', controlName);
    _required(host.presentation.isBuy, 'host.presentation.isBuy', 'function', controlName);
    _required(host.presentation.typeText, 'host.presentation.typeText', 'function', controlName);
    _required(host.presentation.statusText, 'host.presentation.statusText', 'function', controlName);
    _required(host.presentation.timeText, 'host.presentation.timeText', 'function', controlName);
    _required(host.presentation.sideClass, 'host.presentation.sideClass', 'function', controlName);
    _required(host.presentation.pnlClass, 'host.presentation.pnlClass', 'function', controlName);
    _required(host.presentation.canvasPalette, 'host.presentation.canvasPalette', 'function', controlName);

    _required(host.preferences, 'host.preferences', 'object', controlName);
    _required(host.preferences.get, 'host.preferences.get', 'function', controlName);
    _required(host.preferences.set, 'host.preferences.set', 'function', controlName);

    _required(host.cache, 'host.cache', 'object', controlName);
    _required(host.cache.get, 'host.cache.get', 'function', controlName);
    _required(host.cache.set, 'host.cache.set', 'function', controlName);

    _required(host.trading, 'host.trading', 'object', controlName);
    _required(host.trading.api, 'host.trading.api', 'object', controlName);
    // The calls, not just the objects holding them. An `api: {}` is the shape a
    // half-wired host actually has — the object gets built, the methods get
    // forgotten — so stopping at the object would skip exactly the members this
    // function exists to catch.
    _required(host.trading.api.getExecutions, 'host.trading.api.getExecutions', 'function', controlName);
    _required(host.trading.api.searchInstruments, 'host.trading.api.searchInstruments', 'function', controlName);
    _required(host.trading.marketData, 'host.trading.marketData', 'object', controlName);
    _required(host.trading.marketData.addSymbol, 'host.trading.marketData.addSymbol', 'function', controlName);
    _required(host.trading.marketData.removeSymbol, 'host.trading.marketData.removeSymbol', 'function', controlName);
    _required(host.trading.marketData.resubscribe, 'host.trading.marketData.resubscribe', 'function', controlName);
    _required(host.trading.marketData.getOrders, 'host.trading.marketData.getOrders', 'function', controlName);
    _required(host.trading.portfolioId, 'host.trading.portfolioId', 'function', controlName);
    _required(host.trading.pickInstrument, 'host.trading.pickInstrument', 'function', controlName);
    _required(host.trading.priceStep, 'host.trading.priceStep', 'function', controlName);

    _required(host.ticker, 'host.ticker', 'object', controlName);
    _required(host.ticker.publish, 'host.ticker.publish', 'function', controlName);

    _required(host.allow, 'host.allow', 'function', controlName);
    _required(host.log, 'host.log', 'function', controlName);

    _required(host.close, 'host.close', 'function', controlName);
    _required(host.spawn, 'host.spawn', 'function', controlName);
    _required(host.persistState, 'host.persistState', 'function', controlName);
    _required(host.saveLayout, 'host.saveLayout', 'function', controlName);

    _required(host.register, 'host.register', 'function', controlName);
    _required(host.unregister, 'host.unregister', 'function', controlName);
    _required(host.broadcast, 'host.broadcast', 'function', controlName);

    return host;
}

// `typeof null` is 'object', which would let a null store through the object
// checks — the one case worth spelling out, because null is exactly what an
// unwired dependency looks like.
function _required(value: unknown, path: string, kind: string, controlName: string): void {
    if (value === null || value === undefined || typeof value !== kind)
        throw new Error(`${controlName}: ${path} is required`);
}
